import { useCallback, useMemo } from 'react';
import { Activity } from '@models/activity.model';
import { useActivitiesByOrganizationIdProjectId } from '@hooks/api';
import { PaginationActions } from '../index';

interface UseProjectFeedByOrganizationProps {
  organizationId: string;
  projectId?: string;
  projectIds?: string[];
}

/**
 * Activity feed scoped to an organization (subcontractors page), paginated by
 * keyset cursor via useActivitiesByOrganizationIdProjectId. Accumulation is a
 * plain flatMap over the fetched pages — keyset never overlaps or skips rows.
 */
export const useProjectFeedByOrganization = ({
  organizationId,
  projectId,
  projectIds,
}: UseProjectFeedByOrganizationProps) => {
  const {
    data: currentData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useActivitiesByOrganizationIdProjectId({
    organizationId,
    projectId,
    projectIds,
    limit: 10,
  });

  const activities = useMemo<Activity[]>(
    () => currentData?.pages?.flatMap((page: any) => page.items as Activity[]) ?? [],
    [currentData],
  );

  const totalCount = useMemo(
    () => currentData?.pages?.[0]?.total ?? 0,
    [currentData],
  );

  const loadMore = useCallback(() => {
    if (isLoading || isFetchingNextPage || !hasNextPage) return;
    fetchNextPage();
  }, [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]);

  const paginationActions: PaginationActions = {
    loadMore,
    hasMore: Boolean(hasNextPage),
    isLoading: isLoading && activities.length === 0,
    isFetchingMore: isFetchingNextPage,
  };

  return {
    activities,
    paginationActions,
    totalCount,
    refetch,
  };
};
